import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import RatingStars from '@repo/ui/src/components/RatingStars';
import InlineRecipeCardInfo from '@components/InlineRecipe/_InlineRecipeCardInfo';
import InlineIngredientSummary from '@components/InlineRecipe/_InlineIngredientSummary';
import PrintButton from '@views/RecipeDetailPage/_PrintButton';
import { useCreateRecipeTime } from '@hooks/recipe/useCreateRecipeTime';
import { UnitOfMeasureEnum } from '@enums/recipe-enums';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import KnifeAndFork from '@repo/ui/src/components/Icons/KnifeAndFork';
import {
  AuthorProps,
  IngredientSummaryProps,
  MetaProps,
  RecipeMetaSectionProps,
  RecipeStepProps,
} from '@repo/ui/src/types';

export interface InlineRecipeProps {
  id: string;
  recipeMetaSection: RecipeMetaSectionProps;
  author?: AuthorProps;
  ratingSummary: {
    average: number;
    count: number;
  };
  ingredientSummary?: IngredientSummaryProps;
  steps: Array<RecipeStepProps>;
  _meta: MetaProps;
}
const InlineRecipe = ({
  recipeMetaSection,
  author,
  ratingSummary,
  ingredientSummary,
  steps,
  _meta,
  id,
}: InlineRecipeProps) => {
  const { t } = useTranslation();
  const cookTime = useCreateRecipeTime(recipeMetaSection?.cookTime);
  const prepTime = useCreateRecipeTime(recipeMetaSection?.prepTime);

  const authorName = author?.name || 'Traeger Kitchen';

  const segmentRecipe: any = {
    recipe_id: id,
    recipe_name: recipeMetaSection?.title,
    recipe_url: `https://traeger.com/${_meta?.deliveryKey}`,
    recipe_author_name: authorName,
    pellet_recommendation: recipeMetaSection?.hardwood,
    recipe_serving_size: recipeMetaSection.servingSizeMin,
    recipe_measurement_units: 'Imperial',
    recipe_prep_time: recipeMetaSection?.prepTime,
    recipe_cook_time: recipeMetaSection?.cookTime,
  };

  return (
    <section className="mt-12 md:mt-20">
      <div
        className="bg-warm-100 px-5 py-10 text-center border-2 border-b-0 border-warm-300 md:pb-12"
        id="inline-recipe-container"
      >
        <h5 className="tra-text-web-heading-5">{recipeMetaSection.title}</h5>
        <p className="tra-text-web-body-s py-5">by {authorName}</p>
        {ratingSummary?.average && (
          <RatingStars
            value={ratingSummary?.average}
            reviewCount={ratingSummary.count}
            className="justify-center"
          />
        )}
        <InlineRecipeCardInfo {...recipeMetaSection} prepTime={prepTime} cookTime={cookTime} />
      </div>
      <div className="px-4 md:px-10 pt-10 pb-20 border-2 border-warm-300">
        <div className="flex flex-col md:flex-row justify-center gap-x-10 gap-y-4 pb-10">
          <PrintButton
            servingSize={recipeMetaSection?.servingSizeMin}
            authorName={authorName}
            ingredientSummary={ingredientSummary}
            unitOfMeasure={UnitOfMeasureEnum.Imperial}
            recipeMetaSection={recipeMetaSection}
            prepTime={prepTime}
            cookTime={cookTime}
            steps={steps}
            segmentRecipe={segmentRecipe}
          />
          <Button
            as={Anchor}
            variant={ButtonVariant.secondary}
            link={_meta?.deliveryKey}
            className="inline-flex justify-center items-center w-full md:w-auto"
          >
            <KnifeAndFork className="h-6 w-6 mr-2" />
            {t('recipeContent.viewRecipe', undefined, { default: 'View Recipe' })}
          </Button>
        </div>
        <p className="tra-text-web-body-l">{recipeMetaSection?.description}</p>
        <h6 className="tra-text-web-heading-6 pt-6 pb-[28px]">
          {t('recipeContent.ingredientsLabel')}
        </h6>
        {ingredientSummary?.groups?.map((group, i) => (
          <InlineIngredientSummary {...group} key={i} />
        ))}
        <ul className="list-none pt-20 pl-0">
          <h6 className="tra-text-web-heading-6 pb-6">{t('recipeContent.stepsLabel')}</h6>
          {steps?.map((step, i) => (
            <li key={i} data-test-id={`recipe_step_${i + 1}`} className="list-none pb-6 last:pb-0">
              <div className="flex">
                <p className="tra-text-web-heading-7 pr-6">{i + 1}</p>
                <p className="tra-text-web-body-l">{step?.instruction}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default InlineRecipe;
